/**
 * Router Configuration
 *
 * Defines routes for the application using TanStack Router.
 * Handles protected routes and authentication redirects.
 */

import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router'
import { isAuthenticated } from '@/api/services/auth.service'
import Login from '@/components/pages/login'
import Home from '@/components/pages/home'
import Embed from '@/components/pages/embed'
import NotFound from '@/components/pages/not-found'

// Root route - provides layout wrapper
const rootRoute = createRootRoute({
  component: () => <Outlet />,
})

// Login route - redirect to home if already authenticated
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  beforeLoad: () => {
    if (isAuthenticated()) {
      throw redirect({ to: '/' })
    }
  },
  component: Login,
})

// Home route (protected) - redirect to login if not authenticated
const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' })
    }
  },
  component: Home,
})

// Embedded editor - opened in a host app's iframe (Mary's spread ladder). Not guarded: the page
// signs in with the session the host hands over (see components/pages/embed).
const embedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/embed',
  // The router parses `?spreadId=123` as a number, so normalize to the string spread ids use.
  validateSearch: (search: Record<string, unknown>): { spreadId: string } => ({
    spreadId:
      typeof search.spreadId === 'string' || typeof search.spreadId === 'number'
        ? String(search.spreadId).trim()
        : '',
  }),
  component: Embed,
})

// Create route tree
const routeTree = rootRoute.addChildren([loginRoute, homeRoute, embedRoute])

// Create router instance
export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultNotFoundComponent: NotFound,
})

// Register router for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
